import { barAvatarPlugin } from './bar-avatar-plugin'
import { BarChart } from '../../../../../../components/Charts/BarChart'
import { BoxChart } from '../../../../../../components/Charts/BoxChart'
import { endOfDay, startOfDay } from 'date-fns'
import { formatCurrency } from '../../format-currency'
import { formatTasks } from '../../format-tasks'
import { MAP_COLOR_BY_PROVIDER } from '../../../../../../components/__common/map-color-by-provider'
import { ProviderName, providers } from '@indigohive/cogfy-providers'
import { TooltipItem } from 'chart.js'
import { useCogfy } from '../../../../../../hooks'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

const owners = ['cogfy', 'customer', 'record'] as const

export type ProvidersUsagesChartProps = {
  connectionOwner: typeof owners[number][]
  startDate?: Date
  endDate?: Date
}

export function ProvidersUsagesChart (props: ProvidersUsagesChartProps) {
  const { connectionOwner, startDate, endDate } = props

  const cogfy = useCogfy()
  const { t } = useTranslation()
  const query = {
    startDate: startDate?.toISOString() ?? startOfDay(new Date()).toISOString(),
    endDate: endDate?.toISOString() ?? endOfDay(new Date()).toISOString(),
    connectionOwner
  }

  const getProvidersUsages = useQuery({
    queryKey: ['getProvidersUsages', query] as const,
    queryFn: ({ signal }) => cogfy.getProvidersUsages(query, { signal })
  })

  const providerUsages = getProvidersUsages.data

  const data = {
    labels: providerUsages?.map(p => p.provider) ?? [],
    providerNames: providerUsages?.map(p => p.provider),
    datasets: [
      {
        label: '',
        data: providerUsages?.map(usage => usage.connectionOwner === 'cogfy' ? usage.credits : usage.costs) ?? [],
        tasks: providerUsages?.map(usage => usage.tasks),
        currencies: providerUsages?.map(usage => usage.currency),
        backgroundColor: providerUsages?.map(usage => MAP_COLOR_BY_PROVIDER[usage.provider as ProviderName]).filter(color => color !== undefined) ?? '#000000'
      }
    ]
  }

  return (
    <>
      <BoxChart title={t('workspaceUsagePage:Usage by providers')}>
        <BarChart
          className="min-h-[300px]"
          data={data}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            layout: {
              padding: {
                top: 40
              }
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  title: (tooltip) => (providers.find(provider => provider.name === tooltip.at(0)?.label))?.label,
                  label: (tooltip) => {
                    const isCogfyConnection = connectionOwner.includes('cogfy')
                    const label = ' ' + (isCogfyConnection ? t('Credits') : t('workspaceUsagePage:Costs')) + '      '

                    if (isCogfyConnection) {
                      return label + tooltip.formattedValue
                    } else {
                      return label + formatCurrency(data.datasets[0].currencies?.[tooltip.dataIndex] as 'USD' | 'BRL' | 'EUR' ?? 'USD', tooltip.parsed.y)
                    }
                  },
                  afterLabel: (tooltip: TooltipItem<'bar'> & { dataset: { tasks: number [] } }) => ' Tasks: ' + formatTasks(tooltip.dataset.tasks[tooltip.dataIndex], 'standard')
                }
              }
            },
            scales: {
              x: {
                stacked: true,
                grid: { color: 'transparent' },
                ticks: {
                  callback: (val) => providers.find(provider => provider.name === data.labels[val as number])?.label ?? ''
                }
              },
              y: { stacked: true }
            }
          }}
          loading={getProvidersUsages.isLoading}
          error={getProvidersUsages.isError}
          plugins={[barAvatarPlugin]}
        />
      </BoxChart>
    </>
  )
}
